import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import * as signals from "../../../store/signals";
import { CSRF_HEADER, fetchJson, getCSRFToken } from "../../../utils/ajax";

/**
 * Starts a new basket for the currently assisted customer.
 *
 * This operation:
 * 1. Loads the customer's current basket
 * 2. Freezes the old basket (preserving it for records)
 * 3. Creates a new empty basket for the customer
 * 4. Reloads the page to show the new basket
 *
 * This is useful when a CSR needs to create a fresh order for a customer
 * while preserving their existing cart items in a frozen state.
 *
 * Side effects:
 * - Emits `onBeforeStartNewBasket` signal before starting
 * - Emits `onAfterStartNewBasket` signal with the new basket after completion
 * - The page is typically reloaded after this operation completes
 *
 * @returns Promise that resolves when the new basket is created and loaded
 *
 * @example
 * dispatch(startNewBasket())
 */
export const startNewBasket = createAsyncThunk(
    "csr/startNewBasket",
    async (): Promise<void> => {
        signals.csr.onBeforeStartNewBasket.emit();
        const basketOld = await basketAPI.load();
        await fetchJson(`/api/csr/baskets/${basketOld.id}/freeze/`, {
            method: "POST",
            headers: {
                [CSRF_HEADER]: await getCSRFToken(),
            },
        });
        const basketNew = await basketAPI.load();
        signals.csr.onAfterStartNewBasket.emit(basketNew);
    },
);
